
import { db, serverTimestamp } from "../firebase";
import "firebase/compat/auth";

export type ActivityModule =
  | "contract"
  | "contractReview"
  | "legalMatter"
  | "brandAsset"
  | "ipProtection"
  | "govProject"
  | "template"
  | "coreDocument"
  | "calendar"
  | "govCorrespondence"
  | "task"
  | "seal"
  | "lawyer"
  | "auth"
  | "STAMP_REQUEST"
  | "other";

export type ActivityAction =
  | "create"
  | "update"
  | "delete"
  | "statusChange"
  | "uploadAttachment"
  | "import"
  | "export"
  | "login"
  | "logout"
  | "start_flow"
  | "reset_flow"
  | "approve"
  | "reject"
  | "submit";

export interface ActivityLogEntry {
  id?: string;
  module: ActivityModule;
  action: ActivityAction | string;
  entityId: string;
  entityType: string;
  entityTitle?: string;
  actorId?: string;
  actorName?: string;
  actorDepartment?: string;
  before?: any;
  after?: any;
  extra?: Record<string, any>;
  createdAt?: any;
  
  userId?: string;
  userEmail?: string;
  targetType?: string;
  targetId?: string;
  details?: string;
  ipAddress?: string; // Legacy
  ip?: string;        // New Standard
  clientIp?: string;  // Legacy
}

const GET_CALLER_IP_URL = "https://asia-east1-kevin-erp-demo.cloudfunctions.net/getCallerIp";

/**
 * Fetches the client's public IP address.
 * 1. Checks localStorage (Fastest, set by AuthContext via CF trace).
 * 2. Fallback to Backend API (GET request).
 */
export async function fetchClientIp(): Promise<string> {
  // 1. Try Cache first (Set by AuthContext on load)
  if (typeof window !== "undefined") {
      const cached = window.localStorage.getItem("clientIp");
      if (cached && cached !== "unknown") {
          return cached;
      }
  }

  // 2. Fallback to API if cache missing
  try {
    const res = await fetch(GET_CALLER_IP_URL, {
      method: "GET"
      // credentials: "omit" is default
    });

    if (!res.ok) {
      console.warn(`[ActivityLog] getCallerIp failed: ${res.status}`);
      return "unknown";
    }

    const data = await res.json();
    const ip = data.ip || "unknown";
    
    // Update cache
    if (ip !== "unknown" && typeof window !== "undefined") {
        window.localStorage.setItem("clientIp", ip);
    }
    return ip;
  } catch (err) {
    console.error("[ActivityLog] getCallerIp error", err);
    return "unknown";
  }
}

export async function logActivity(params: {
  userId?: string;
  userEmail?: string;
  action: string;
  targetType?: string;
  targetId?: string;
  details?: string;
  module?: ActivityModule;
  entityType?: string;
  entityId?: string;
  entityTitle?: string;
  actorId?: string;
  actorName?: string;
  actorDepartment?: string;
  extra?: any;
}): Promise<void> {
  try {
    // 1. Get IP (From Cache or API)
    const ip = await fetchClientIp();

    // 2. Construct Entry
    const entry: any = {
      ip: ip,
      clientIp: ip, // legacy field compat
      action: params.action,
      createdAt: serverTimestamp(),
    };

    // Merge normalized fields
    if (params.userId) entry.userId = params.userId;
    if (params.userEmail) entry.userEmail = params.userEmail;
    if (params.targetType) entry.targetType = params.targetType;
    if (params.targetId) entry.targetId = params.targetId;
    if (params.details) entry.details = params.details;

    // Backward compatibility & Mapping
    if (params.module) entry.module = params.module;
    if (params.entityType) { entry.entityType = params.entityType; entry.targetType = params.entityType; }
    if (params.entityId) { entry.entityId = params.entityId; entry.targetId = params.entityId; }
    if (params.entityTitle) entry.entityTitle = params.entityTitle;
    if (params.actorId) { entry.actorId = params.actorId; entry.userId = params.actorId; }
    if (params.actorName) entry.actorName = params.actorName;
    if (params.actorDepartment) entry.actorDepartment = params.actorDepartment;
    if (params.extra) entry.extra = params.extra;

    // 3. Write directly to Firestore
    await db.collection("activity_logs").add(entry);

  } catch (e) {
    console.error("[ActivityLog] Failed to write log", e);
  }
}

export async function logLogin(user: { uid: string; email?: string | null }): Promise<void> {
  await logActivity({
    userId: user.uid,
    userEmail: user.email || undefined,
    action: "login",
    targetType: "system",
    details: "User logged in",
    module: "auth",
    actorId: user.uid,
    actorName: user.email || "User"
  });
}

export async function logLogout(user: { uid: string; email?: string | null }): Promise<void> {
  await logActivity({
    userId: user.uid,
    userEmail: user.email || undefined,
    action: "logout",
    targetType: "system",
    details: "User logged out",
    module: "auth",
    actorId: user.uid,
    actorName: user.email || "User"
  });
}
